const RATE_LIMIT_BACKOFF_STEPS_MS = [30000, 60000, 120000];

const getHeaderValue = (headers, name) => {
  if (!headers) return "";
  if (typeof headers.get === "function") {
    return headers.get(name) || headers.get(name.toLowerCase()) || "";
  }
  return headers[name] || headers[name.toLowerCase()] || "";
};

export const normalizeUserApiPath = (url = "") => {
  if (!url) return "";
  const withoutQuery = url.split("?")[0];
  const marker = "/api/v1/user";
  const markerIndex = withoutQuery.indexOf(marker);
  if (markerIndex >= 0) {
    return withoutQuery.slice(markerIndex + marker.length) || "/";
  }
  return withoutQuery;
};

const endpointBackoffKey = (config = {}) => {
  const method = String(config.method || "GET").toUpperCase();
  const path = normalizeUserApiPath(config.url || "");
  return path ? `${method} ${path}` : "";
};

export const createEndpointRateLimitBackoff = ({ now = Date.now } = {}) => {
  const stateByEndpoint = new Map();

  const parseRetryAfterMs = (headers) => {
    const raw = getHeaderValue(headers, "Retry-After");
    if (!raw) return 0;
    const seconds = Number(raw);
    if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
    const time = new Date(raw).getTime();
    return Number.isFinite(time) ? Math.max(0, time - now()) : 0;
  };

  const buildBackoffError = (config, state) => {
    const remainingSeconds = Math.max(
      1,
      Math.ceil((Number(state.blockedUntil || 0) - now()) / 1000),
    );
    const error = new Error(`Rate limit backoff active for ${remainingSeconds}s`);
    error.config = config;
    error.__rateLimitBackoff = true;
    error.response = {
      status: 429,
      data: {
        status: false,
        message: `요청이 너무 많습니다. ${remainingSeconds}초 후 다시 시도해주세요.`,
      },
      headers: { "retry-after": String(remainingSeconds) },
    };
    return error;
  };

  const assertRequestAllowed = (config = {}) => {
    const key = endpointBackoffKey(config);
    if (!key) return;
    const state = stateByEndpoint.get(key);
    if (!state || Number(state.blockedUntil || 0) <= now()) return;
    throw buildBackoffError(config, state);
  };

  const registerServerRateLimit = (config = {}, headers = {}) => {
    const key = endpointBackoffKey(config);
    if (!key) return;
    const previous = stateByEndpoint.get(key) || { failures: 0, blockedUntil: 0 };
    const failures = previous.failures + 1;
    const stepMs =
      RATE_LIMIT_BACKOFF_STEPS_MS[
        Math.min(failures - 1, RATE_LIMIT_BACKOFF_STEPS_MS.length - 1)
      ];
    stateByEndpoint.set(key, {
      failures,
      blockedUntil: now() + Math.max(stepMs, parseRetryAfterMs(headers)),
    });
  };

  const resetAfterSuccess = (config = {}) => {
    const key = endpointBackoffKey(config);
    if (key) stateByEndpoint.delete(key);
  };

  const isSyntheticRateLimitError = (error) => error?.__rateLimitBackoff === true;

  const getState = (config = {}) => stateByEndpoint.get(endpointBackoffKey(config));

  return {
    assertRequestAllowed,
    getState,
    isSyntheticRateLimitError,
    registerServerRateLimit,
    resetAfterSuccess,
  };
};

export const endpointRateLimitBackoff = createEndpointRateLimitBackoff();
